import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { ProductCardHorizontal as PalProductCardHorizontal } from 'web-palette/dist/components/ProductCardHorizontal/ProductCardHorizontal';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import Image from '@ulta/core/components/Image/Image';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './ProductCardHorizontal';

/**
  * Represents an ProductCardHorizontal component
  * @method
  * @param {ProductCardHorizontalProps} props - React properties passed from composition
  * @returns PalProductCardHorizontal
  */
const ProductCardHorizontal = ( props ) => {
  const {
    style, // rail or list style

    /* actions */
    action = defaultProps.action,
    addToBagAction, // This is for rail
    fullyClickable, // boolean if card can be fully clickable

    /* image */
    image,
    imageProps,
    prefetchImage,

    /* productVariant */
    variantImage,
    variantTypeDescription = defaultProps.variantTypeDescription,
    variantTypeLabel = defaultProps.variantTypeLabel,

    /* price */
    kitPrice,
    kitPriceAccessibilityLabel,
    listPrice = defaultProps.listPrice,
    listPriceAccessibilityLabel,
    originalPriceLabel,
    priceLabel,
    salePrice = defaultProps.salePrice,
    salePriceAccessibilityLabel,

    /* reviews */
    rating,
    reviewAccessibilityLabel,
    reviewCount,

    /* text */
    brandName = defaultProps.brandName,
    htmlTag,
    productName = defaultProps.productName,
    tagMessage,

    /* tags */
    tagsBottom
  } = props;

  /** variables */
  const productCardHorizontalRef = useRef( null );
  const hasImage = image?.imageUrl;
  const screenReaderText = `${brandName} ${productName}`;

  /** Objects for external media and actions coming from Amplience */
  let formattedAction;
  let formattedActionFullyClickableable;
  let formattedMedia;

  /** Accessibility labels */
  const accessibilityLabelKitPrice = interpolateString( kitPriceAccessibilityLabel, [kitPrice] );
  const accessibilityLabelListPrice = interpolateString( listPriceAccessibilityLabel, [listPrice] );
  const accessibilityLabelOriginalPrice = interpolateString( originalPriceLabel, [listPrice] );
  const accessibilityLabelSalePrice = interpolateString( salePriceAccessibilityLabel, [salePrice] );
  const accessibilityRating = interpolateString( reviewAccessibilityLabel, [rating, reviewCount] );

  /** Formatters for incoming media, tags and actions */
  if( hasImage ){
    formattedMedia = utils.createImage( {
      image: image.imageUrl,
      imageProps,
      prefetchImage,
      screenReaderText
    } );
  }

  if( action && !fullyClickable ){
    formattedAction = actionFormatter( {
      action: action,
      size: 'compact'
    }, { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  if( action && fullyClickable ){
    formattedActionFullyClickableable = actionFormatter( {
      action: action,
      fullyClickableParent: fullyClickable,
      textForScreenReaders: screenReaderText
    }, { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  useIntersectionProcessor( {
    ...props,
    componentRef: productCardHorizontalRef
  } );

  return (
    <div
      className='ProductCardHorizontal'
      ref={ productCardHorizontalRef }
    >
      <PalProductCardHorizontal
        brandName={ brandName }
        externalActionElement={ formattedActionFullyClickableable }
        externalFooterActionElement={ formattedAction }
        externalMediaElement={ formattedMedia }
        fullyClickableParent={ fullyClickable }
        htmlTag={ htmlTag }
        kitPrice={ kitPrice }
        kitPriceAccessibilityLabel={ accessibilityLabelKitPrice }
        listPrice={ listPrice }
        listPriceAccessibilityLabel={ accessibilityLabelListPrice }
        originalPriceLabel={ accessibilityLabelOriginalPrice }
        priceLabel={ priceLabel }
        productName={ productName }
        rating={ rating }
        reviewAccessibilityLabel={ accessibilityRating }
        reviewCount={ reviewCount }
        salePrice={ salePrice }
        salePriceAccessibilityLabel={ accessibilityLabelSalePrice }
        style={ style }
        swatchImage={ variantImage }
        tagMessage={ tagMessage }
        tagsBottom={ tagsBottom }
        variantTypeDescription={ variantTypeDescription }
        variantTypeLabel={ variantTypeLabel }
      />
    </div>
  );
};

/**
 * createImage function for creating the image for the card
 * @method
 * @param {Object} data - The data object for creating the image
 */
export const createImage = ( data ) => {
  const {
    image,
    imageProps,
    imgDimension,
    prefetchImage,
    screenReaderText
  } = data;

  if( !image ){
    return;
  }

  return (
    <Image
      altText={ screenReaderText }
      ariaHidden={ true }
      aspectRatio={ '1:1' }
      metaData={ { width: imgDimension, height: imgDimension } }
      prefetch={ prefetchImage }
      shouldUseIntersectionObserver={ false }
      src={ image }
      { ...spreadProps( { ...imageProps } ) }
    />
  );
};

/**
 * Property type definitions
 * @typedef ProductCardHorizontalProps
 * @type {object}
 * @property {object} action - button action
 * @property {object} addToBagAction - addToBagAction action for rail
 * @property {string} brandName - brand name
 * @property {boolean} fullyClickable - boolean for fullyClickable card
 * @property {string} image - product image
 * @property {object} imageProps - Image props for Product image - helps override the default props passed
 * @property {string} kitPrice -  kit price
 * @property {string} kitPriceAccessibilityLabel - accessibility label for kit price
 * @property {string} listPrice - list price
 * @property {string} listPriceAccessibilityLabel - accessibility label for list price
 * @property {string} originalPriceLabel - accessibility label for original price
 * @property {string} priceLabel - price label
 * @property {string} productName - product name
 * @property {number} rating - rating for review stars
 * @property {string} reviewAccessibilityLabel - accessibility label for review stars
 * @property {number} reviewCount - review count for review stars
 * @property {string} salePriceAccessibilityLabel - accessibility label for sale price
 * @property {string} salePrice - sale price
 * @property {string} style = rail or list style
 * @property {array} tagsBottom = array of tags
 * @property {string} tagMessage = text for tag
 * @property {string} variantImage - sets the image url for product
 * @property {string} variantTypeDescription - variant label
 * @property {string} variantTypeLabel - variant type
 */
export const propTypes = {
  /** Button action of PC */
  action: PropTypes.shape( {
    graphql: PropTypes.string,
    label: PropTypes.string
  } ),
  /** Button action of PC */
  addToBagAction: PropTypes.object,
  /** brand name for the product */
  brandName: PropTypes.string.isRequired,
  /** boolean for fullyClickable card */
  fullyClickable: PropTypes.bool,
  /** product image */
  image: PropTypes.object,
  /** Image props for Product image - helps override the default props passed */
  imageProps: PropTypes.object,
  /** list price */
  listPrice: PropTypes.string,
  /** accessibility label for list price */
  listPriceAccessibilityLabel: PropTypes.string,
  /** accessibility label for original price */
  originalPriceLabel: PropTypes.string,
  /** price label */
  priceLabel: PropTypes.string,
  /** product name */
  productName: PropTypes.string.isRequired,
  /** rating for review stars */
  rating: PropTypes.number,
  /** accessibility label for review stars */
  reviewAccessibilityLabel: PropTypes.string,
  /** review count for review stars */
  reviewCount: PropTypes.number,
  /** sale price */
  salePrice: PropTypes.string,
  /** accessibility label for sale price */
  salePriceAccessibilityLabel: PropTypes.string,
  /** rail or list style */
  style: PropTypes.string,
  /** array of tags */
  tagsBottom: PropTypes.array,
  /** text for tag */
  tagMessage: PropTypes.string,
  /** sets the image url for product */
  variantImage: PropTypes.shape( {
    imageUrl: PropTypes.string
  } ),
  /** variant label */
  variantTypeDescription: PropTypes.string,
  /** variant type */
  variantTypeLabel: PropTypes.string
};

/**
  * Default values for passed properties
  *
  * @type {object}
  * @property {object} action='null' - The default action
  */
export const defaultProps = {
  action: null
};

ProductCardHorizontal.propTypes = propTypes;
ProductCardHorizontal.defaultProps = defaultProps;
export default ProductCardHorizontal;